import type { AIImageShape } from "../../app/services/aiImage";
import type { CropData } from "./cropUtils";

/**
 * 画像クロップの比率の制約。比率はすべて「幅 / 高さ」の数値で表す
 * （1 より大きいと横長、小さいと縦長）。
 *
 * - `fixed`: 比率を固定する（切り替え UI なし）
 * - `presets`: 決めた選択肢から選ぶ
 * - `range`: `min`〜`max` の範囲内なら自由に決められる（プリセット + カスタム）
 * - `free`: 制限なし（`range` の 1:10〜10:1 として扱う）
 */
export type AspectRule =
  | { type: "fixed"; ratio: number }
  | { type: "presets"; options: number[]; initial?: number }
  | {
      type: "range";
      min: number;
      max: number;
      /** 範囲外のプリセットは除外される */
      presets?: number[];
      initial?: number;
      /** 「そのまま」を出す（元画像の比率が範囲内のときだけ選べる） */
      allowOriginal?: boolean;
    }
  | { type: "free" };

/** 用途別の比率の制約。ページ側に比率の数値を直接書かず、ここに 1 行足す */
export const ASPECT_RULES = {
  avatar: { type: "fixed", ratio: 1 },
  /** マイレージのステージカード（横カード。旧実装 mileage_index.js の Cropper 設定と同じ 216:136） */
  mileageCard: { type: "fixed", ratio: 216 / 136 },
  /** ロール選択のカード（縦カード） */
  roleCard: { type: "presets", options: [1 / 1.414, 3 / 4, 1], initial: 1 / 1.414 },
  /** コミッションのサムネイル（横長 1.414:1） */
  commissionThumbnail: { type: "fixed", ratio: 1.414 },
  /** コミッションのキービジュアル */
  commissionKeyVisual: { type: "fixed", ratio: 16 / 9 },
  /** プロジェクトバナー（一覧の縦カード） */
  projectBanner: { type: "fixed", ratio: 1 / 1.414 },
} satisfies Record<string, AspectRule>;

/** `free` で使う範囲とプリセット */
const FREE_MIN = 1 / 10;
const FREE_MAX = 10;
const FREE_PRESETS = [16 / 9, 3 / 2, 1, 2 / 3];

/** 比率を同じとみなす許容差（対数での差） */
const SAME_ASPECT_TOLERANCE = 0.001;
/** 座標から比率を推定するときの許容差（ピクセルの丸めを吸収する） */
const INFER_ASPECT_TOLERANCE = 0.02;

/** エディタで選んでいる比率 */
export type AspectSelection =
  | { mode: "preset"; aspect: number }
  | { mode: "custom"; aspect: number }
  | { mode: "original" };

const logDistance = (a: number, b: number): number =>
  Math.abs(Math.log(a) - Math.log(b));

export const isSameAspect = (
  a: number,
  b: number,
  tolerance: number = SAME_ASPECT_TOLERANCE,
): boolean => logDistance(a, b) <= tolerance;

/** 選べる比率の下限と上限 */
export const getAspectBounds = (rule: AspectRule): { min: number; max: number } => {
  switch (rule.type) {
    case "fixed":
      return { min: rule.ratio, max: rule.ratio };
    case "presets":
      return { min: Math.min(...rule.options), max: Math.max(...rule.options) };
    case "range":
      return { min: rule.min, max: rule.max };
    case "free":
      return { min: FREE_MIN, max: FREE_MAX };
  }
};

/** 比率を範囲内に丸める */
export const clampAspect = (aspect: number, rule: AspectRule): number => {
  const { min, max } = getAspectBounds(rule);
  return Math.min(max, Math.max(min, aspect));
};

/** 切り替え UI に並べるプリセット（範囲外は除外） */
export const getAspectPresets = (rule: AspectRule): number[] => {
  switch (rule.type) {
    case "fixed":
      return [rule.ratio];
    case "presets":
      return rule.options;
    case "range": {
      const { min, max } = getAspectBounds(rule);
      return (rule.presets ?? []).filter(
        (p) => p >= min * (1 - SAME_ASPECT_TOLERANCE) && p <= max * (1 + SAME_ASPECT_TOLERANCE),
      );
    }
    case "free":
      return FREE_PRESETS;
  }
};

/** 範囲内で自由に比率を決められるか */
export const allowsCustomAspect = (rule: AspectRule): boolean =>
  rule.type === "range" || rule.type === "free";

/** 「そのまま」を出す設定か（実際に選べるかは元画像の比率による） */
export const allowsOriginalAspect = (rule: AspectRule): boolean =>
  rule.type === "free" || (rule.type === "range" && Boolean(rule.allowOriginal));

/** 元画像の比率で「そのまま」を選べるか */
export const canUseOriginalAspect = (
  rule: AspectRule,
  imageAspect: number | null,
): boolean => {
  if (!allowsOriginalAspect(rule)) return false;
  if (imageAspect === null) return true;
  const { min, max } = getAspectBounds(rule);
  return imageAspect >= min && imageAspect <= max;
};

/** 一番近いプリセット（対数での距離） */
export const findNearestPreset = (aspect: number, presets: number[]): number | null => {
  let nearest: number | null = null;
  for (const preset of presets) {
    if (nearest === null || logDistance(aspect, preset) < logDistance(aspect, nearest)) {
      nearest = preset;
    }
  }
  return nearest;
};

/** 任意の比率を、制約の中で選べる比率に寄せる */
export const selectionForAspect = (
  aspect: number,
  rule: AspectRule,
  tolerance: number = SAME_ASPECT_TOLERANCE,
): AspectSelection => {
  const clamped = clampAspect(aspect, rule);
  const presets = getAspectPresets(rule);
  const nearest = findNearestPreset(clamped, presets);
  if (nearest !== null && isSameAspect(clamped, nearest, tolerance)) {
    return { mode: "preset", aspect: nearest };
  }
  if (allowsCustomAspect(rule)) {
    return { mode: "custom", aspect: clamped };
  }
  return { mode: "preset", aspect: nearest ?? clamped };
};

/**
 * エディタを開いたときの比率。
 *
 * 1. 前回の切り抜き座標があれば、保存された `aspect` で復元する
 *    （古いデータで `aspect` がなければ座標の縦横比から推定する）
 * 2. なければ制約の初期値（`initial` / 固定値 / 先頭のプリセット）
 * 3. `free` で初期値がなければ「そのまま」
 */
export const getInitialAspectSelection = (
  rule: AspectRule,
  cropData?: CropData | null,
): AspectSelection => {
  if (cropData && cropData.width > 0 && cropData.height > 0) {
    if (cropData.aspect === null) {
      if (allowsOriginalAspect(rule)) return { mode: "original" };
    } else if (typeof cropData.aspect === "number" && cropData.aspect > 0) {
      return selectionForAspect(cropData.aspect, rule);
    } else {
      return selectionForAspect(cropData.width / cropData.height, rule, INFER_ASPECT_TOLERANCE);
    }
  }

  switch (rule.type) {
    case "fixed":
      return { mode: "preset", aspect: rule.ratio };
    case "presets":
    case "range": {
      const initial = rule.initial ?? getAspectPresets(rule)[0];
      if (initial !== undefined) return selectionForAspect(initial, rule);
      return { mode: "custom", aspect: clampAspect(1, rule) };
    }
    case "free":
      return { mode: "original" };
  }
};

/**
 * 「そのまま」が元画像の比率では選べないときに、範囲内で一番近い比率へ寄せる。
 * それ以外はそのまま返す。
 */
export const resolveSelectionForImage = (
  selection: AspectSelection,
  rule: AspectRule,
  imageAspect: number,
): AspectSelection => {
  if (selection.mode !== "original" || canUseOriginalAspect(rule, imageAspect)) {
    return selection;
  }
  return selectionForAspect(imageAspect, rule);
};

/** 比率の表示（例: 16:9 / 5:1 / 1:1.414 / 4.2:1） */
export const formatAspect = (aspect: number): string => {
  for (let height = 1; height <= 16; height++) {
    const width = Math.round(aspect * height);
    if (width >= 1 && width <= 16 && isSameAspect(aspect, width / height)) {
      return `${width}:${height}`;
    }
  }
  const trim = (value: number) => String(Number(value.toFixed(3)));
  return aspect >= 1 ? `${trim(aspect)}:1` : `1:${trim(1 / aspect)}`;
};

/** カスタム比率スライダーの位置（0〜1）。横長と縦長が対称になるよう対数で刻む */
export const aspectToSliderPosition = (aspect: number, rule: AspectRule): number => {
  const { min, max } = getAspectBounds(rule);
  if (max <= min) return 0;
  return (Math.log(clampAspect(aspect, rule)) - Math.log(min)) / (Math.log(max) - Math.log(min));
};

export const sliderPositionToAspect = (position: number, rule: AspectRule): number => {
  const { min, max } = getAspectBounds(rule);
  const t = Math.min(1, Math.max(0, position));
  return Math.exp(Math.log(min) + t * (Math.log(max) - Math.log(min)));
};

/** AI 生成の出力形状（比率が null のときはシーンの既定に任せる） */
export const imageShapeForAspect = (aspect: number | null): AIImageShape | undefined => {
  if (aspect === null) return undefined;
  if (aspect > 1.15) return "landscape";
  if (aspect < 0.87) return "portrait";
  return "square";
};
